import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useGetReleveDevoirsByIdQuery,
  useValiderLigneDevoirsMutation,
  useValiderToutDevoirsMutation,
  useSupprimerLigneDevoirsMutation,
  useRemigrerNotesDevoirsMutation,
  LigneReleveDevoirsOCR,
} from '../../services/ocrApi';
import { API_URL } from '../../services/baseApi';
import { formatNote } from '../../utils/formatNote';
import {
  Eye,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader,
  ChevronLeft,
  ChevronRight,
  Check,
  RotateCcw,
  ZoomIn,
  ZoomOut,
  FileImage,
  X,
  CheckCheck,
  Trash2,
  Search,
} from 'lucide-react';
import { PanneauEtudiantsManquants } from './PanneauEtudiantsManquants';

const VerificationOCRDevoirs: React.FC = () => {
  const { releveId } = useParams<{ releveId: string }>();
  const navigate = useNavigate();

  // State
  const [selectedLigneIndex, setSelectedLigneIndex] = useState(0);
  const [editedValues, setEditedValues] = useState<Record<number, {
    note_d1?: number | null;
    absent_d1?: boolean;
    note_d2?: number | null;
    absent_d2?: boolean;
    commentaire?: string;
  }>>({});
  const [zoomLevel, setZoomLevel] = useState(100);
  const [showOnlyToVerify, setShowOnlyToVerify] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showFullImage, setShowFullImage] = useState(false);
  const [recherche, setRecherche] = useState('');
  const [lignePourSuppression, setLignePourSuppression] = useState<LigneReleveDevoirsOCR | null>(null);
  // Mode édition inline dans la zone OCR sombre
  const [enEdition, setEnEdition] = useState(false);
  // Ref pour focus auto sur D1 dès qu'on entre en édition
  const d1InputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (enEdition && d1InputRef.current) {
      // setTimeout 0 pour laisser React render l'input avant de focus
      const t = setTimeout(() => {
        d1InputRef.current?.focus();
        d1InputRef.current?.select();
      }, 0);
      return () => clearTimeout(t);
    }
  }, [enEdition]);
  // Optimistic UI : overrides locaux pour affichage instantané (mirror examens V2)
  type LigneOverride = Partial<Pick<LigneReleveDevoirsOCR,
    'note_d1_validee' | 'note_d1_detectee' | 'absent_d1'
    | 'note_d2_validee' | 'note_d2_detectee' | 'absent_d2'
    | 'verifie' | 'necessite_verification'>>;
  const [ligneOverrides, setLigneOverrides] = useState<Record<number, LigneOverride>>({});

  // Queries et Mutations
  const { data: releve, isLoading, refetch } = useGetReleveDevoirsByIdQuery(
    parseInt(releveId || '0'),
    { skip: !releveId }
  );
  const [validerLigne, { isLoading: validerLoading }] = useValiderLigneDevoirsMutation();
  const [validerTout, { isLoading: validerToutLoading }] = useValiderToutDevoirsMutation();
  const [supprimerLigne, { isLoading: supprLoading }] = useSupprimerLigneDevoirsMutation();
  const [remigrerNotes, { isLoading: remigrerLoading }] = useRemigrerNotesDevoirsMutation();

  // Mode lecture seule pour les relevés validés (consultation post-validation)
  const isValideStatus = releve?.status === 'valide';
  // Mode "Réviser" : permet d'éditer un relevé déjà validé (les corrections
  // doivent ensuite être poussées en BDD via le bouton "Re-migrer notes").
  const [forceEditMode, setForceEditMode] = useState(false);
  const isReadOnly = isValideStatus && !forceEditMode;

  // Toutes les lignes (avec overrides locaux pour UI instantanée)
  const lignesRaw = releve?.lignes_ocr || [];
  const lignes = React.useMemo(() => {
    return lignesRaw.map(l => {
      const o = ligneOverrides[l.id];
      return o ? { ...l, ...o } as LigneReleveDevoirsOCR : l;
    });
  }, [lignesRaw, ligneOverrides]);

  const lignesFiltrees = lignes.filter((l) => {
    if (showOnlyToVerify && (!l.necessite_verification || l.verifie)) return false;
    if (recherche.trim()) {
      const q = recherche.trim().toLowerCase();
      const hay = [
        l.matricule_detecte,
        l.etudiant_nom,
        l.etudiant_matricule,
        String(l.numero_ligne),
      ].join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  const ligneCourante = lignesFiltrees[selectedLigneIndex];

  // Compteur affiché : sur les lignes filtrées hors recherche (= total ou à vérifier)
  const lignesVisibles = showOnlyToVerify
    ? lignes.filter((l) => l.necessite_verification && !l.verifie)
    : lignes;

  // Sortir du mode édition quand on change de ligne
  useEffect(() => {
    setEnEdition(false);
  }, [ligneCourante?.id]);

  // currentValues est dérivé directement de ligneCourante (avec fallback)
  // puis fusionné avec les éventuelles modifications locales (editedValues).
  // → Garantit que currentValues n'est JAMAIS undefined tant que ligneCourante existe,
  //   ce qui évite le clignotement du panneau lors de l'auto-avance vers une ligne
  //   pas encore initialisée dans editedValues (incident reporté 2026-04-30).
  const currentValues = React.useMemo(() => {
    if (!ligneCourante) return null;
    const base = {
      note_d1: (ligneCourante.note_d1_validee ?? ligneCourante.note_d1_detectee) as number | null,
      absent_d1: !!ligneCourante.absent_d1,
      note_d2: (ligneCourante.note_d2_validee ?? ligneCourante.note_d2_detectee) as number | null,
      absent_d2: !!ligneCourante.absent_d2,
      commentaire: ligneCourante.commentaire || '',
    };
    const edited = editedValues[ligneCourante.id];
    return edited ? { ...base, ...edited } : base;
  }, [ligneCourante, editedValues]);

  // Navigation
  const handlePrevious = () => {
    if (selectedLigneIndex > 0) setSelectedLigneIndex(selectedLigneIndex - 1);
  };

  const handleNext = () => {
    if (selectedLigneIndex < lignesFiltrees.length - 1) setSelectedLigneIndex(selectedLigneIndex + 1);
  };

  // Navigation au clavier (flèches)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignorer si focus dans un input/textarea/select
      const target = e.target as HTMLElement;
      const isEditing = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable;

      // ↑ ou ← : ligne précédente
      if (e.key === 'ArrowUp' || (e.key === 'ArrowLeft' && !isEditing)) {
        e.preventDefault();
        if (selectedLigneIndex > 0) setSelectedLigneIndex(selectedLigneIndex - 1);
      }
      // ↓ ou → : ligne suivante
      else if (e.key === 'ArrowDown' || (e.key === 'ArrowRight' && !isEditing)) {
        e.preventDefault();
        if (selectedLigneIndex < lignesFiltrees.length - 1) setSelectedLigneIndex(selectedLigneIndex + 1);
      }
      // Home : première ligne
      else if (e.key === 'Home' && !isEditing) {
        e.preventDefault();
        setSelectedLigneIndex(0);
      }
      // End : dernière ligne
      else if (e.key === 'End' && !isEditing) {
        e.preventDefault();
        setSelectedLigneIndex(lignesFiltrees.length - 1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedLigneIndex, lignesFiltrees.length]);

  // Mise à jour des valeurs
  const updateValue = (field: string, value: any) => {
    if (!ligneCourante) return;
    setEditedValues((prev) => ({
      ...prev,
      [ligneCourante.id]: {
        ...prev[ligneCourante.id],
        [field]: value,
      },
    }));
  };

  // Valider une ligne avec UI INSTANTANÉE (optimistic update + mutation en background).
  // Pattern emprunté à VerificationOCRCorrectionV2 examens : pas d'attente du serveur,
  // l'utilisateur voit la confirmation prise en compte immédiatement.
  const handleValiderLigne = async (_advanceToNext: boolean = true) => {
    if (!ligneCourante || !currentValues || !releveId) return;

    const ligneIdValidee = ligneCourante.id;
    const note_d1: number | undefined = currentValues.absent_d1
      ? undefined
      : (currentValues.note_d1 != null ? currentValues.note_d1 : undefined);
    const note_d2: number | undefined = currentValues.absent_d2
      ? undefined
      : (currentValues.note_d2 != null ? currentValues.note_d2 : undefined);
    const absent_d1 = !!currentValues.absent_d1;
    const absent_d2 = !!currentValues.absent_d2;

    // Validation des bornes : note ∈ [0, 20] (sauf si absent)
    const erreurNote = ((): string | null => {
      if (note_d1 !== undefined && (note_d1 < 0 || note_d1 > 20)) {
        return `Note D1 invalide : ${note_d1} (doit être entre 0 et 20)`;
      }
      if (note_d2 !== undefined && (note_d2 < 0 || note_d2 > 20)) {
        return `Note D2 invalide : ${note_d2} (doit être entre 0 et 20)`;
      }
      return null;
    })();
    if (erreurNote) {
      setMessage({ type: 'error', text: erreurNote });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    // 1. UI INSTANTANÉE : marquer la ligne comme vérifiée + appliquer les valeurs
    setLigneOverrides(prev => ({
      ...prev,
      [ligneIdValidee]: {
        note_d1_validee: note_d1,
        absent_d1,
        note_d2_validee: note_d2,
        absent_d2,
        verifie: true,
        necessite_verification: false,
      },
    }));
    setEnEdition(false);
    setMessage({ type: 'success', text: 'Ligne validée ✓' });
    setTimeout(() => setMessage(null), 1200);

    // 2. Avancer à la PROCHAINE ligne séquentielle (ligne N → ligne N+1).
    // L'utilisateur a explicitement demandé un avancement séquentiel et non
    // un saut vers la prochaine ligne "à vérifier" (qui pouvait sauter de la
    // ligne 1 à la ligne 15 et tromper l'opérateur).
    if (selectedLigneIndex + 1 < lignesFiltrees.length) {
      setSelectedLigneIndex(selectedLigneIndex + 1);
    }

    // 3. Mutation en background (fire-and-forget avec rollback en cas d'erreur)
    validerLigne({
      releveId: parseInt(releveId),
      data: {
        ligne_id: ligneIdValidee,
        note_d1,
        absent_d1,
        note_d2,
        absent_d2,
        commentaire: currentValues.commentaire,
      },
    })
      .unwrap()
      .catch((error: any) => {
        // Rollback : retirer l'override pour cette ligne
        setLigneOverrides(prev => {
          const next = { ...prev };
          delete next[ligneIdValidee];
          return next;
        });
        setMessage({
          type: 'error',
          text: error?.data?.error || 'Erreur de validation — note restaurée',
        });
      });
  };

  // Valider la ligne avec des valeurs explicites (utilisé par les boutons rapides 0/0 et D1→D2).
  // Bypass l'état React (currentValues) car les valeurs sont fournies directement.
  const validerAvecValeurs = (params: {
    note_d1: number | undefined;
    absent_d1: boolean;
    note_d2: number | undefined;
    absent_d2: boolean;
    successText: string;
  }) => {
    if (!ligneCourante || !releveId) return;
    const ligneIdValidee = ligneCourante.id;
    const { note_d1, absent_d1, note_d2, absent_d2, successText } = params;

    // Validation des bornes : note ∈ [0, 20]
    if (note_d1 !== undefined && (note_d1 < 0 || note_d1 > 20)) {
      setMessage({ type: 'error', text: `Note D1 invalide : ${note_d1} (doit être entre 0 et 20)` });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    if (note_d2 !== undefined && (note_d2 < 0 || note_d2 > 20)) {
      setMessage({ type: 'error', text: `Note D2 invalide : ${note_d2} (doit être entre 0 et 20)` });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    // 1. Synchroniser le formulaire local
    setEditedValues((prev) => ({
      ...prev,
      [ligneIdValidee]: {
        ...prev[ligneIdValidee],
        note_d1: note_d1 ?? null,
        absent_d1,
        note_d2: note_d2 ?? null,
        absent_d2,
      },
    }));

    // 2. UI INSTANTANÉE
    setLigneOverrides((prev) => ({
      ...prev,
      [ligneIdValidee]: {
        note_d1_validee: note_d1,
        absent_d1,
        note_d2_validee: note_d2,
        absent_d2,
        verifie: true,
        necessite_verification: false,
      },
    }));
    setEnEdition(false);
    setMessage({ type: 'success', text: successText });
    setTimeout(() => setMessage(null), 1200);

    // 3. Avancer à la PROCHAINE ligne séquentielle (cohérent avec handleValiderLigne)
    if (selectedLigneIndex + 1 < lignesFiltrees.length) {
      setSelectedLigneIndex(selectedLigneIndex + 1);
    }

    // 4. Mutation en background
    validerLigne({
      releveId: parseInt(releveId),
      data: { ligne_id: ligneIdValidee, note_d1, absent_d1, note_d2, absent_d2 },
    })
      .unwrap()
      .catch((error: any) => {
        setLigneOverrides((prev) => {
          const next = { ...prev };
          delete next[ligneIdValidee];
          return next;
        });
        setMessage({
          type: 'error',
          text: error?.data?.error || 'Erreur de validation — note restaurée',
        });
      });
  };

  // Bouton rapide : remplir D1 et D2 avec 0 puis valider (étudiant absent du devoir)
  const handleZeroEtValider = () =>
    validerAvecValeurs({
      note_d1: 0,
      absent_d1: false,
      note_d2: 0,
      absent_d2: false,
      successText: '0 / 0 validé ✓',
    });

  // Bouton rapide : copier D1 vers D2 puis valider
  const handleCopierD1VersD2EtValider = () => {
    if (!currentValues || currentValues.note_d1 == null) return;
    validerAvecValeurs({
      note_d1: currentValues.note_d1,
      absent_d1: !!currentValues.absent_d1,
      note_d2: currentValues.note_d1,
      absent_d2: false,
      successText: `D2 = D1 (${currentValues.note_d1}) validé ✓`,
    });
  };

  // Valider tout le relevé
  const handleValiderTout = async () => {
    if (!releveId) return;

    try {
      await validerTout(parseInt(releveId)).unwrap();
      setMessage({ type: 'success', text: 'Relevé validé et notes enregistrées avec succès' });
      setTimeout(() => navigate('/ocr/devoirs'), 1500);
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.data?.error || 'Erreur lors de la validation du relevé',
      });
    }
  };

  // Re-migrer les notes vers la BDD (utile apres correction d'une ligne deja validee)
  const handleRemigrer = async () => {
    if (!releveId) return;
    if (!window.confirm(
      `Re-migrer les notes OCR vers la base de données ?\n\n` +
      `Cette action ré-écrit les NoteDevoir avec les valeurs actuelles des lignes OCR ` +
      `(notes validées ou détectées). Utile après correction de lignes sur un relevé déjà validé.\n\n` +
      `→ Les corrections faites en saisie manuelle hors OCR pourront être écrasées.`
    )) return;
    try {
      const res = await remigrerNotes(parseInt(releveId)).unwrap();
      const r = res.result || {};
      const total = (r.notes_creees || 0) + (r.notes_maj || 0);
      setMessage({
        type: 'success',
        text: `${total} note(s) re-migrée(s) — ${r.notes_creees || 0} créée(s), ${r.notes_maj || 0} modifiée(s)`
              + ((r.notes_protegees || 0) > 0 ? ` · ${r.notes_protegees} protégée(s) (cross-option)` : ''),
      });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.data?.error || 'Erreur lors de la re-migration',
      });
    }
  };

  // Supprimer la ligne (matricule en trop, doublon, ligne fantôme)
  const handleSupprimerLigne = async () => {
    if (!lignePourSuppression || !releveId) return;
    try {
      const res = await supprimerLigne({
        releveId: parseInt(releveId),
        ligneId: lignePourSuppression.id,
      }).unwrap();
      setLignePourSuppression(null);
      setMessage({ type: 'success', text: res.message });
      // Garder l'index dans les bornes après suppression
      setSelectedLigneIndex((idx) => Math.max(0, Math.min(idx, lignesFiltrees.length - 2)));
      refetch();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.data?.error || 'Erreur lors de la suppression',
      });
    }
  };

  // Reset aux valeurs détectées
  const handleReset = () => {
    if (!ligneCourante) return;
    setEditedValues((prev) => ({
      ...prev,
      [ligneCourante.id]: {
        note_d1: ligneCourante.note_d1_detectee,
        absent_d1: ligneCourante.absent_d1,
        note_d2: ligneCourante.note_d2_detectee,
        absent_d2: ligneCourante.absent_d2,
        commentaire: '',
      },
    }));
  };

  // Indicateur de confiance (valeurs 0-100)
  const getConfianceColor = (confiance: number) => {
    if (confiance >= 90) return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400';
    if (confiance >= 70) return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400';
    return 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400';
  };

  // URL du scan complet
  const getScanUrl = useCallback(() => {
    if (!releve?.fichier_scan) return null;
    if (releve.fichier_scan.startsWith('http')) return releve.fichier_scan;
    return `${API_URL.replace('/api', '')}${releve.fichier_scan.startsWith('/') ? '' : '/'}${releve.fichier_scan}`;
  }, [releve]);

  // Stats
  const nbVerifiees = lignes.filter(l => l.verifie).length;
  const nbAVerifier = lignes.filter(l => l.necessite_verification && !l.verifie).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader className="animate-spin text-blue-600" size={48} />
      </div>
    );
  }

  if (!releve) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 p-4 rounded-lg">
          Relevé non trouvé
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-100 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/ocr/devoirs')}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              <ChevronLeft size={24} />
            </button>
            <div>
              <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center">
                <Eye className="mr-2" size={22} />
                {isReadOnly ? 'Consultation' : (isValideStatus && forceEditMode ? 'Révision OCR' : 'Vérification OCR')} - {releve.code_releve}
                {isReadOnly && (
                  <span className="ml-3 px-2 py-0.5 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full">
                    Validé · Lecture seule
                  </span>
                )}
                {isValideStatus && forceEditMode && (
                  <span className="ml-3 px-2 py-0.5 text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-full">
                    🔓 Révision · modifications non encore migrées
                  </span>
                )}
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {releve.matiere_code} - <span className="font-semibold text-blue-600 dark:text-blue-400">{releve.matiere_nom}</span> | {releve.niveau} {releve.option}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {/* Voir le relevé complet */}
            {releve.fichier_scan && (
              <button
                onClick={() => setShowFullImage(true)}
                className="flex items-center px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                title="Voir le relevé complet"
              >
                <FileImage size={16} className="mr-1" />
                Relevé complet
              </button>
            )}

            {/* Toggle filter */}
            <label className="flex items-center space-x-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={showOnlyToVerify}
                onChange={(e) => {
                  setShowOnlyToVerify(e.target.checked);
                  setSelectedLigneIndex(0);
                }}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              <span className="dark:text-gray-300">A vérifier uniquement</span>
            </label>

            {/* Stats */}
            <div className="text-sm text-gray-600 dark:text-gray-400 flex items-center space-x-2">
              <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-xs">
                {nbVerifiees} validées
              </span>
              {nbAVerifier > 0 && (
                <span className="px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded text-xs">
                  {nbAVerifier} à vérifier
                </span>
              )}
              <span className="text-xs">{lignes.length} total</span>
            </div>

            {/* Valider tout (caché en mode consultation) */}
            {!isReadOnly && (
              <button
                onClick={handleValiderTout}
                disabled={validerToutLoading}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {validerToutLoading ? (
                  <Loader className="animate-spin mr-2" size={16} />
                ) : (
                  <CheckCheck className="mr-2" size={16} />
                )}
                Tout valider
              </button>
            )}

            {/* Boutons mode "relevé validé" — Réviser + Re-migrer */}
            {isValideStatus && (
              <>
                {!forceEditMode ? (
                  <button
                    onClick={() => {
                      setForceEditMode(true);
                      setMessage({
                        type: 'success',
                        text: '🔓 Mode révision activé — corrige les lignes nécessaires, puis clique sur "Re-migrer notes" pour pousser les corrections en BDD.',
                      });
                    }}
                    title="Déverrouiller la modification d'un relevé déjà validé"
                    className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm"
                  >
                    <Eye className="mr-2" size={16} />
                    Réviser
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setForceEditMode(false);
                      setEnEdition(false);
                      setMessage({ type: 'success', text: '🔒 Mode révision désactivé.' });
                    }}
                    title="Reverrouiller la consultation"
                    className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-sm"
                  >
                    🔒 Quitter révision
                  </button>
                )}
                <button
                  onClick={handleRemigrer}
                  disabled={remigrerLoading}
                  title="Re-migrer les notes OCR vers la base de données (après correction post-validation)"
                  className="flex items-center px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 text-sm"
                >
                  {remigrerLoading ? (
                    <Loader className="animate-spin mr-2" size={16} />
                  ) : (
                    <RotateCcw className="mr-2" size={16} />
                  )}
                  Re-migrer notes
                </button>
              </>
            )}
          </div>
        </div>

        {/* Message */}
        {message && (
          <div
            className={`mt-3 p-2 rounded-lg flex items-center text-sm ${
              message.type === 'success'
                ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300'
                : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300'
            }`}
          >
            {message.type === 'success' ? <CheckCircle2 className="mr-2" size={16} /> : <XCircle className="mr-2" size={16} />}
            {message.text}
            <button onClick={() => setMessage(null)} className="ml-auto"><X size={14} /></button>
          </div>
        )}
      </div>

      {/* Panneau étudiants manquants (codes-barres non lus) — collapsible */}
      {releveId && (
        <PanneauEtudiantsManquants
          releveId={parseInt(releveId, 10)}
          disabled={isReadOnly}
          onLigneCreee={() => refetch()}
        />
      )}

      {/* Main content */}
      {lignesVisibles.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <CheckCircle2 className="mx-auto text-green-500" size={64} />
            <h2 className="mt-4 text-xl font-semibold text-gray-900 dark:text-gray-100">
              {showOnlyToVerify ? 'Toutes les lignes ont été vérifiées' : 'Aucune ligne détectée'}
            </h2>
            {showOnlyToVerify && (
              <button
                onClick={() => setShowOnlyToVerify(false)}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Voir toutes les lignes
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex-1 flex overflow-hidden">
          {/* ═══ PANNEAU CENTRAL : Image + Saisie + Actions (tout scrollable) ═══ */}
          <div className="flex-1 overflow-y-auto bg-gray-800 dark:bg-gray-950">
            {ligneCourante && currentValues && (
              <>
                {/* Zoom controls (sticky top) */}
                <div className="sticky top-0 z-10 flex items-center justify-between px-3 py-1.5 bg-gray-900/90 backdrop-blur-sm">
                  <span className="text-white text-xs">
                    Ligne {ligneCourante.numero_ligne} (Page {ligneCourante.numero_page})
                  </span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setZoomLevel(Math.max(50, zoomLevel - 25))} className="p-0.5 text-white/70 hover:text-white"><ZoomOut size={14} /></button>
                    <span className="text-white/70 text-[10px] w-7 text-center">{zoomLevel}%</span>
                    <button onClick={() => setZoomLevel(Math.min(300, zoomLevel + 25))} className="p-0.5 text-white/70 hover:text-white"><ZoomIn size={14} /></button>
                  </div>
                </div>

                {/* Image + Zone OCR alignées (même largeur, centrées) */}
                <div className="p-3">
                  <div
                    className="mx-auto"
                    style={{ width: `${zoomLevel}%`, maxWidth: '100%' }}
                  >
                    {ligneCourante.image_base64 ? (
                      <img
                        src={`data:image/jpeg;base64,${ligneCourante.image_base64}`}
                        alt={`Ligne ${ligneCourante.numero_ligne}`}
                        className="block w-full border border-gray-600 rounded-t"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-32 text-gray-400 border border-gray-600 rounded-t">
                        Aucune image disponible
                      </div>
                    )}

                    {/* Résultat OCR ÉDITABLE — directement sous l'image, MÊME LARGEUR
                        Layout aligné sur les colonnes du relevé :
                        Matricule (5-23%) | Nom (vide ici) | D1 (50-71%) | D2 (71-92%) | Vérif */}
                    <div className="bg-gray-900 dark:bg-black px-3 py-2 border border-t-0 border-gray-700 rounded-b space-y-1.5">
                  {/* Ligne 1 : grille alignée sur les colonnes du PDF
                      [N°ø] [Matricule] [Nom·ø] [D1] [D2] [Vérif] */}
                  <div
                    style={{ display: 'grid', gridTemplateColumns: '5% 18% 28% 20% 20% 9%', alignItems: 'center', gap: '4px' }}
                  >
                    {/* Col 1 : N° (vide ici, juste un placeholder pour l'alignement) */}
                    <span></span>

                    {/* Col 2 : Matricule + confiance */}
                    <div className="flex items-center gap-1 justify-center">
                      <span className="font-mono text-xs font-bold text-blue-400 truncate" title={ligneCourante.matricule_detecte || 'N/A'}>
                        {ligneCourante.matricule_detecte || 'N/A'}
                      </span>
                      <span className={`px-1 py-0.5 rounded text-[9px] font-medium ${getConfianceColor(Number(ligneCourante.confiance_matricule))}`}>
                        {Number(ligneCourante.confiance_matricule).toFixed(0)}%
                      </span>
                    </div>

                    {/* Col 3 : Nom étudiant (sous la colonne nom) */}
                    <div className="text-[10px] text-gray-400 truncate text-center" title={ligneCourante.etudiant_nom || ''}>
                      {ligneCourante.etudiant_nom || ''}
                    </div>

                    {/* Col 4 : D1 — éditable inline */}
                    <div className="flex items-center gap-1 justify-center">
                      <span className="text-blue-300 text-xs">D1:</span>
                      {enEdition && !isReadOnly ? (
                        <>
                          <input
                            ref={d1InputRef}
                            type="number" min="0" max="20" step="0.25"
                            value={currentValues.note_d1 ?? ''}
                            onChange={(e) => updateValue('note_d1', e.target.value ? parseFloat(e.target.value) : null)}
                            disabled={!!currentValues.absent_d1}
                            className="w-12 px-1 py-0.5 text-sm font-bold text-center border border-blue-400 rounded bg-gray-800 text-white focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-50"
                          />
                          <label className="flex items-center gap-0.5 text-[10px] text-gray-300 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={currentValues.absent_d1 || false}
                              onChange={(e) => updateValue('absent_d1', e.target.checked)}
                              className="rounded"
                            />
                            AB
                          </label>
                        </>
                      ) : (
                        <>
                          <span className={`text-base font-extrabold ${currentValues.absent_d1 ? 'text-orange-400' : (currentValues.note_d1 !== null && currentValues.note_d1 !== undefined ? 'text-green-400' : 'text-red-400')}`}>
                            {currentValues.absent_d1 ? 'AB' : formatNote(currentValues.note_d1)}
                          </span>
                          <span className={`px-1 py-0.5 rounded text-[9px] ${getConfianceColor(Number(ligneCourante.confiance_d1))}`}>
                            {Number(ligneCourante.confiance_d1).toFixed(0)}%
                          </span>
                        </>
                      )}
                    </div>

                    {/* Col 5 : D2 — éditable inline */}
                    <div className="flex items-center gap-1 justify-center">
                      <span className="text-purple-300 text-xs">D2:</span>
                      {enEdition && !isReadOnly ? (
                        <>
                          <input
                            type="number" min="0" max="20" step="0.25"
                            value={currentValues.note_d2 ?? ''}
                            onChange={(e) => updateValue('note_d2', e.target.value ? parseFloat(e.target.value) : null)}
                            disabled={!!currentValues.absent_d2}
                            className="w-12 px-1 py-0.5 text-sm font-bold text-center border border-purple-400 rounded bg-gray-800 text-white focus:outline-none focus:ring-2 focus:ring-purple-400 disabled:opacity-50"
                          />
                          <label className="flex items-center gap-0.5 text-[10px] text-gray-300 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={currentValues.absent_d2 || false}
                              onChange={(e) => updateValue('absent_d2', e.target.checked)}
                              className="rounded"
                            />
                            AB
                          </label>
                        </>
                      ) : (
                        <>
                          <span className={`text-base font-extrabold ${currentValues.absent_d2 ? 'text-orange-400' : (currentValues.note_d2 !== null && currentValues.note_d2 !== undefined ? 'text-green-400' : 'text-red-400')}`}>
                            {currentValues.absent_d2 ? 'AB' : formatNote(currentValues.note_d2)}
                          </span>
                          <span className={`px-1 py-0.5 rounded text-[9px] ${getConfianceColor(Number(ligneCourante.confiance_d2))}`}>
                            {Number(ligneCourante.confiance_d2).toFixed(0)}%
                          </span>
                        </>
                      )}
                    </div>

                    {/* Col 6 : Statut */}
                    <div className="flex justify-center">
                      {ligneCourante.verifie ? <CheckCircle2 size={14} className="text-green-400" /> : ligneCourante.necessite_verification ? <AlertTriangle size={14} className="text-orange-400" /> : <CheckCircle2 size={14} className="text-blue-400" />}
                    </div>
                  </div>

                  {/* Ligne 2 : Boutons d'action (le nom est déjà dans la grille au-dessus) */}
                  <div className="flex items-center justify-end gap-2">
                    {ligneCourante.etudiant_matricule && (
                      <span className="text-[10px] text-gray-500 font-mono mr-auto">{ligneCourante.etudiant_matricule}</span>
                    )}
                    {!isReadOnly && (
                      <div className="flex items-center gap-1.5">
                        {enEdition ? (
                          <>
                            <button
                              onClick={() => {
                                updateValue('note_d1', 0);
                                updateValue('absent_d1', false);
                                updateValue('note_d2', 0);
                                updateValue('absent_d2', false);
                              }}
                              title="Mettre 0 dans D1 et D2 (étudiant n'a pas participé)"
                              className="flex items-center gap-1 px-2 py-1 bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 hover:bg-orange-200 dark:hover:bg-orange-800/60 rounded text-[11px] font-semibold"
                            >
                              0 / 0
                            </button>
                            <button
                              onClick={() => {
                                const v = currentValues.note_d1;
                                if (v != null) {
                                  updateValue('note_d2', v);
                                  updateValue('absent_d2', false);
                                }
                              }}
                              disabled={currentValues.note_d1 == null}
                              title="Copier la note D1 vers D2"
                              className="flex items-center gap-1 px-2 py-1 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-800/60 rounded text-[11px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              D1 → D2
                            </button>
                            <button
                              onClick={() => handleValiderLigne(true)}
                              disabled={validerLoading}
                              className="flex items-center gap-1 px-2.5 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-[11px] font-semibold disabled:opacity-60"
                            >
                              {validerLoading ? <Loader size={11} className="animate-spin" /> : <Check size={11} />} OK
                            </button>
                            <button
                              onClick={() => { setEnEdition(false); handleReset(); }}
                              className="px-2 py-1 text-[11px] text-gray-300 hover:text-white border border-gray-600 rounded"
                            >
                              Annuler
                            </button>
                          </>
                        ) : (
                          <>
                            {ligneCourante.verifie && (
                              <span className="flex items-center gap-1 px-2 py-0.5 bg-green-900/40 text-green-400 rounded text-[10px] font-semibold border border-green-700/50">
                                <CheckCircle2 size={10} /> Confirmée
                              </span>
                            )}
                            {/* Bouton rapide 0/0 : applique 0 sur les 2 devoirs et valide en un clic */}
                            {!ligneCourante.verifie && (
                              <button
                                onClick={handleZeroEtValider}
                                disabled={validerLoading}
                                title="Mettre 0 dans D1 et D2 puis valider (étudiant n'a pas participé)"
                                className="flex items-center gap-1 px-2.5 py-1 bg-orange-500 hover:bg-orange-600 text-white rounded text-[11px] font-semibold disabled:opacity-60"
                              >
                                0 / 0
                              </button>
                            )}
                            {/* Bouton rapide D1 → D2 : copie la note de D1 vers D2 et valide */}
                            {!ligneCourante.verifie && currentValues?.note_d1 != null && (
                              <button
                                onClick={handleCopierD1VersD2EtValider}
                                disabled={validerLoading}
                                title={`Copier D1 (${currentValues.note_d1}) vers D2 et valider`}
                                className="flex items-center gap-1 px-2.5 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded text-[11px] font-semibold disabled:opacity-60"
                              >
                                D1 → D2
                              </button>
                            )}
                            {ligneCourante.necessite_verification && !ligneCourante.verifie && (
                              <button
                                onClick={() => handleValiderLigne(false)}
                                disabled={validerLoading}
                                className="flex items-center gap-1 px-2.5 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-[11px] font-semibold disabled:opacity-60"
                                title="Confirmer cette ligne (sans modifier)"
                              >
                                {validerLoading ? <Loader size={11} className="animate-spin" /> : <CheckCheck size={11} />} Confirmer
                              </button>
                            )}
                            <button
                              onClick={() => setEnEdition(true)}
                              className="flex items-center gap-1 px-2.5 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 hover:bg-blue-200 rounded text-[11px] font-semibold"
                            >
                              <Eye size={11} /> Corriger
                            </button>
                            <button
                              onClick={() => setLignePourSuppression(ligneCourante)}
                              className="flex items-center gap-1 px-2 py-1 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 hover:bg-red-200 rounded text-[11px] font-semibold"
                              title="Supprimer cette ligne"
                            >
                              <Trash2 size={11} />
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                  </div>
                </div>

                {/* Zone compacte sous l'OCR : navigation + erreur (fond clair) */}
                <div className="bg-white dark:bg-gray-800 px-3 py-2 border-t dark:border-gray-700">
                  {/* Erreur OCR éventuelle */}
                  {ligneCourante.erreur && (
                    <div className="mb-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-2">
                      <div className="flex items-center text-red-700 dark:text-red-300 text-xs">
                        <AlertTriangle className="mr-1.5" size={12} />
                        {ligneCourante.erreur}
                      </div>
                    </div>
                  )}

                  {/* Navigation compacte */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handlePrevious}
                        disabled={selectedLigneIndex === 0}
                        className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40"
                        title="Précédent (↑ ou ←)"
                      >
                        <ChevronLeft size={14} /> Préc.
                      </button>
                      <span className="text-xs text-gray-500 dark:text-gray-400 font-medium tabular-nums">
                        {selectedLigneIndex + 1} / {lignesFiltrees.length}
                      </span>
                      <button
                        onClick={handleNext}
                        disabled={selectedLigneIndex === lignesFiltrees.length - 1}
                        className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40"
                        title="Suivant (↓ ou →)"
                      >
                        Suiv. <ChevronRight size={14} />
                      </button>
                    </div>
                    <div className="text-[10px] text-gray-400 dark:text-gray-500 flex items-center gap-1">
                      <kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-[9px]">↑↓</kbd>
                      <span>nav</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* ═══ PANNEAU DROIT : Liste verticale des matricules ═══ */}
          <div className="w-80 bg-white dark:bg-gray-800 border-l dark:border-gray-700 overflow-y-auto shrink-0">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700 z-10">
              <div className="px-3 pt-2 pb-1 flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                  Étudiants ({lignesFiltrees.length})
                  <span className="text-gray-400 ml-1">· total {lignesVisibles.length}</span>
                </span>
              </div>
              <div className="px-3 pb-2">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" size={12} />
                  <input
                    type="text"
                    value={recherche}
                    onChange={e => setRecherche(e.target.value)}
                    placeholder="Rechercher matricule, nom..."
                    className="w-full pl-7 pr-7 py-1 text-[11px] border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                  />
                  {recherche && (
                    <button
                      onClick={() => setRecherche('')}
                      className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
                {recherche && (
                  <div className="mt-1 text-[9px] text-gray-400">
                    {lignesFiltrees.length} résultat{lignesFiltrees.length > 1 ? 's' : ''} sur {lignesVisibles.length}
                  </div>
                )}
              </div>
            </div>

            <div className="divide-y divide-gray-100 dark:divide-gray-700/30">
              {lignesFiltrees.length === 0 && recherche && (
                <div className="px-3 py-4 text-center text-xs text-gray-400">
                  Aucun résultat pour "<strong>{recherche}</strong>"
                </div>
              )}
              {lignesFiltrees.map((l, idx) => {
                const isSel = idx === selectedLigneIndex;
                return (
                  <div
                    key={l.id}
                    onClick={() => setSelectedLigneIndex(idx)}
                    className={`px-3 py-2 cursor-pointer transition-colors ${
                      isSel
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/30 border-l-4 border-transparent'
                    }`}
                  >
                    {/* Ligne 1 : N° + Matricule + Statut */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-[10px] text-gray-400 w-5 shrink-0">{l.numero_ligne}</span>
                        <span className="font-mono text-xs font-bold text-gray-800 dark:text-gray-200 truncate">{l.matricule_detecte || '—'}</span>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {l.verifie ? <CheckCircle2 size={11} className="text-green-500" /> : l.necessite_verification ? <AlertTriangle size={11} className="text-orange-500" /> : <CheckCircle2 size={11} className="text-blue-400" />}
                      </div>
                    </div>
                    {/* Ligne 2 : Nom étudiant */}
                    {l.etudiant_nom && (
                      <div className="mt-0.5 pl-7 text-[10px] text-gray-500 dark:text-gray-400 truncate" title={l.etudiant_nom}>
                        {l.etudiant_nom}
                      </div>
                    )}
                    {/* Ligne 3 : D1 + D2 */}
                    <div className="mt-0.5 pl-7 flex items-center gap-2 text-[10px]">
                      <span className="flex items-center gap-0.5">
                        <span className="text-blue-500 font-semibold">D1:</span>
                        <span className={`font-bold ${l.note_d1_detectee !== null && l.note_d1_detectee !== undefined ? 'text-gray-900 dark:text-gray-100' : 'text-red-500'}`}>
                          {l.absent_d1 ? 'AB' : formatNote(l.note_d1_detectee)}
                        </span>
                      </span>
                      <span className="flex items-center gap-0.5">
                        <span className="text-purple-500 font-semibold">D2:</span>
                        <span className={`font-bold ${l.note_d2_detectee !== null && l.note_d2_detectee !== undefined ? 'text-gray-900 dark:text-gray-100' : 'text-red-500'}`}>
                          {l.absent_d2 ? 'AB' : formatNote(l.note_d2_detectee)}
                        </span>
                      </span>
                    </div>
                    {/* Actions rapides sur la ligne sélectionnée */}
                    {isSel && !isReadOnly && (
                      <div className="mt-1.5 pl-7 flex items-center gap-1 flex-wrap">
                        {l.necessite_verification && !l.verifie && (
                          <button onClick={(e) => { e.stopPropagation(); handleValiderLigne(false); }} disabled={validerLoading}
                            className="flex items-center gap-1 px-2 py-0.5 bg-green-600 hover:bg-green-700 text-white rounded text-[10px] font-medium disabled:opacity-60">
                            {validerLoading ? <Loader size={10} className="animate-spin" /> : <Check size={10} />} Valider
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); setLignePourSuppression(l); }}
                          className="flex items-center gap-1 px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-200 rounded text-[10px] font-medium"
                          title="Supprimer (matricule en trop, doublon)"
                        >
                          <Trash2 size={10} /> Supprimer
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmation suppression */}
      {lignePourSuppression && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <Trash2 className="text-red-600" size={24} />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Supprimer la ligne</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">Cette action est irréversible</p>
              </div>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
              Supprimer la ligne {lignePourSuppression.numero_ligne}
              {lignePourSuppression.matricule_detecte && (
                <> (matricule <strong>{lignePourSuppression.matricule_detecte}</strong>)</>
              )} ?
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setLignePourSuppression(null)}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 text-sm"
              >
                Annuler
              </button>
              <button
                onClick={handleSupprimerLigne}
                disabled={supprLoading}
                className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 text-sm"
              >
                {supprLoading ? <Loader className="animate-spin" size={14} /> : <Trash2 size={14} />}
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal : Vue du relevé complet */}
      {showFullImage && releve.fichier_scan && (
        <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center">
          <div className="relative w-full h-full flex flex-col">
            <div className="flex items-center justify-between p-4 bg-gray-900">
              <h3 className="text-white text-lg font-semibold">
                Relevé complet - {releve.code_releve}
              </h3>
              <button
                onClick={() => setShowFullImage(false)}
                className="text-white hover:text-gray-300 p-1"
              >
                <X size={24} />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4 flex justify-center">
              <iframe
                src={getScanUrl() || ''}
                className="w-full max-w-4xl h-full border-0 bg-white"
                title="Relevé complet"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerificationOCRDevoirs;
